/**
 * A QUEDA DE SESSÃO QUE NINGUÉM VIA.
 *
 * 🔴 O QUE MEDIMOS EM 14/08: `app_errors` nos últimos 14 dias tinha ZERO 401 e
 * ZERO 503. Pelo instrumento, o painel estava impecável. E o dono continuava
 * sendo jogado pra tela de login no meio do trabalho.
 *
 * A explicação é que existe um SEGUNDO caminho de logout, inteiro dentro do
 * navegador: `hooks/useAuth` chama `supabase.auth.getSession()`, isso falha (o
 * clássico "Invalid Refresh Token"), o app faz `signOut({scope:"local"})` e cai
 * no login. Nenhuma requisição sai, nenhum status 4xx/5xx acontece, nenhuma
 * linha entra em `app_errors`. O evento é REAL e INVISÍVEL — e por isso o número
 * dizia "tudo bem" com toda a confiança do mundo.
 *
 * Este módulo é o instrumento, não o conserto. Ele não tenta renovar token, não
 * mexe em `createClient`, não adivinha causa: só faz o evento deixar rastro.
 * Consertar o refresh sem medir antes seria apostar — e a casa já perdeu um dia
 * inteiro testando contra código que nem estava no ar (10/08).
 *
 * COMO FUNCIONA, em duas metades:
 *   1. na QUEDA, grava um registro pequeno no localStorage (é o único lugar que
 *      sobrevive: não há sessão pra falar com o servidor);
 *   2. no PRÓXIMO login bem-sucedido, manda os registros pra uma rota
 *      AUTENTICADA (`POST /me/queda-de-sessao`) e só então limpa o localStorage.
 *
 * Rota autenticada de propósito: endpoint aberto de escrita em `app_errors`
 * seria porta de abuso — qualquer um enchendo a tabela que o dono usa pra
 * decidir onde é o defeito.
 */
import { haRascunhoAberto } from "./rascunhoChave";
import { apiFetch } from "./api";
import { TETO_QUEDAS, type QuedaDeSessao } from "../../api/lib/quedaDeSessao";

export type { QuedaDeSessao };
export { TETO_QUEDAS };

export const CHAVE_QUEDA = "queda-de-sessao";

/**
 * O MOTIVO CONHECIDO DA PRÓXIMA QUEDA.
 *
 * Quem desloga nem sempre é quem sabe por quê: o `onError` do React Query
 * (`main.tsx`) chama `supabase.auth.signOut()` direto quando a API devolve 401,
 * e quem OUVE o `SIGNED_OUT` é o `useAuth`, que a essa altura não faz ideia do
 * que aconteceu. Sem isto, toda queda viraria "motivo desconhecido" e o registro
 * não separaria as classes — que é a única coisa que ele existe pra fazer.
 */
let motivoPendente: { texto: string; quando: number } | null = null;

/**
 * 🟥 A MARCA TEM VALIDADE — e isso NÃO é detalhe.
 *
 * Enquanto só o 401 marcava, a marca e a queda aconteciam no mesmo instante: o
 * `signOut()` vem na linha seguinte. Quando a renovação pelo uso passou a marcar
 * também (24/08), apareceu um caso novo: uma renovação pode falhar por REDE às
 * 10h sem matar sessão nenhuma, e a queda de verdade vir às 15h por outro
 * motivo. A marca velha seria consumida por ela — e o registro afirmaria uma
 * causa que não foi a causa.
 *
 * Instrumento que atribui causa errada é pior que instrumento mudo: o mudo faz
 * procurar, o mentiroso faz procurar no lugar errado. Meio minuto é folgado pra
 * quem marca e desloga em seguida, e curto pra não alcançar outro acontecimento.
 */
export const VALIDADE_DO_MOTIVO_MS = 30 * 1000;

export function marcarMotivoDaQueda(motivo: string, agoraMs = Date.now()): void {
  motivoPendente = { texto: motivo, quando: agoraMs };
}

/** Consome o motivo marcado (uma vez só — o próximo evento tem a causa dele). */
export function consumirMotivoDaQueda(agoraMs = Date.now()): string | null {
  const m = motivoPendente;
  motivoPendente = null;
  if (!m) return null;
  // Velha demais pra ser desta queda: melhor "desconhecido" do que a causa errada.
  return agoraMs - m.quando <= VALIDADE_DO_MOTIVO_MS ? m.texto : null;
}

function lerLista(): QuedaDeSessao[] {
  try {
    const cru = localStorage.getItem(CHAVE_QUEDA);
    if (!cru) return [];
    const j = JSON.parse(cru);
    return Array.isArray(j) ? (j as QuedaDeSessao[]) : [];
  } catch {
    return [];
  }
}

/**
 * Registra uma queda. Retorna a lista guardada (útil pro teste e pro chamador
 * saber se o registro pegou).
 *
 * ⚠️ NÃO registra saída PEDIDA pelo usuário — quem clicou em "Sair" não sofreu
 * defeito nenhum, e misturar as duas coisas transformaria o instrumento num
 * contador de uso normal do painel. Quem decide isso é o chamador (`useAuth`),
 * que é o único que sabe se o clique existiu.
 */
export function registrarQueda(q: Omit<QuedaDeSessao, "tinhaRascunho"> & { tinhaRascunho?: boolean }): QuedaDeSessao[] {
  // O ESTADO DO TOKEN é lido AQUI, no instante da queda — e não pelo chamador.
  // Quem registra é quem tem o relógio certo: cinco linhas depois o supabase-js
  // já pode ter apagado a chave (foi assim que a deteção de 15/08 chegou tarde e
  // não viu nada). O chamador ainda pode passar os campos, e aí os dele mandam.
  const estado = sessaoGuardada();
  const registro: QuedaDeSessao = {
    classe: q.classe ?? "queda",
    quando: q.quando,
    motivo: String(q.motivo ?? "").slice(0, 300),
    rota: String(q.rota ?? "").slice(0, 200),
    tinhaRascunho: q.tinhaRascunho ?? haRascunhoAberto(),
    tokenGuardado: q.tokenGuardado ?? estado.havia,
    vencidaHaS: q.vencidaHaS ?? estado.vencidaHaS,
    temRefresh: q.temRefresh ?? estado.temRefresh,
  };
  const lista = [...lerLista(), registro].slice(-TETO_QUEDAS);
  try {
    localStorage.setItem(CHAVE_QUEDA, JSON.stringify(lista));
  } catch {
    /* storage cheio/bloqueado → paciência, o instrumento é best-effort */
  }
  return lista;
}

export function quedasPendentes(): QuedaDeSessao[] {
  return lerLista();
}

/**
 * HAVIA UM TOKEN GUARDADO NESTE NAVEGADOR?
 *
 * 🔴 MEDIDO AO VIVO EM 14/08, e derrubou a primeira versão deste instrumento.
 *
 * Eu tinha amarrado o registro ao `.catch` do `getSession()` — a leitura óbvia do
 * comentário que já existia em `useAuth` ("Token velho/corrompido… ex.: Invalid
 * Refresh Token"). Plantei um token vencido com refresh inválido no navegador de
 * verdade, recarreguei, e o painel fez o que sempre faz: jogou pro /login. E o
 * registro NÃO foi escrito.
 *
 * O `getSession()` do supabase-js NÃO LANÇA nesse caso. Ele devolve
 * `{ data: { session: null }, error: "Refresh token is not valid" }` e emite um
 * `SIGNED_OUT` — que também não ajuda, porque nunca houve sessão nesta aba pra
 * "perder". O `.catch` cobria um caminho que a falha REAL não usa. Um dublê que
 * rejeita a promessa deixa isso verde pra sempre (lição da casa: dublê que não
 * modela a restrição).
 *
 * O sinal honesto é este: havia um token guardado, e mesmo assim não há sessão.
 * Alguém foi deslogado — e é justamente o caso invisível, porque não sai
 * requisição nenhuma.
 *
 * A varredura procura a chave do supabase-js (`sb-<ref>-auth-token`) em vez de
 * ler `supabase.auth.storageKey`, que é propriedade interna e some numa
 * atualização sem avisar.
 */
/**
 * 🟥 O RASTRO QUE NÃO DEPENDE DO SUPABASE — e por que ele precisou existir.
 *
 * MEDIDO EM PRODUÇÃO, 15/08 00:16, com o bundle publicado do #1579: o painel do
 * Ruddy estava aberto e logado; uma navegação depois ele estava no /login, sem
 * `sb-…-auth-token` no localStorage. A queda aconteceu na minha frente — e o
 * instrumento novo NÃO registrou nada.
 *
 * A causa é de TEMPO. A deteção anterior perguntava "havia token guardado?" no
 * efeito do React. Só que o `createClient` liga o auto-refresh na CONSTRUÇÃO do
 * módulo: quando o refresh falha, o supabase-js apaga a própria chave ANTES do
 * React montar o provider. Aí a nossa pergunta chega atrasada, a resposta é
 * "não havia token nenhum", e a queda passa por "aba abrindo deslogada".
 *
 * Ou seja: eu tinha amarrado a deteção a um vestígio que o CULPADO apaga.
 *
 * Este marco é nosso, e ninguém mais mexe nele: enquanto existe sessão viva, ele
 * fica gravado. Se o painel bootar sem sessão e o marco estiver lá, alguém foi
 * deslogado sem pedir — independente do que o supabase-js já tenha limpado, e
 * independente de quanto tempo o boot demorou. Some só em duas situações: o
 * clique em "Sair", e a entrega do registro.
 */
export const CHAVE_SESSAO_VIVA = "sessao-viva-desde";

/** Chamado sempre que uma sessão de verdade carrega. Guarda o PRIMEIRO instante. */
export function marcarSessaoViva(): void {
  try {
    if (!localStorage.getItem(CHAVE_SESSAO_VIVA)) {
      localStorage.setItem(CHAVE_SESSAO_VIVA, new Date().toISOString());
    }
  } catch {
    /* best-effort */
  }
}

/** Some no "Sair" — saída pedida não deixa marco de queda pra trás. */
export function limparSessaoViva(): void {
  try {
    localStorage.removeItem(CHAVE_SESSAO_VIVA);
  } catch {
    /* best-effort */
  }
}

/** Desde quando havia sessão viva neste navegador. null = não havia. */
export function sessaoVivaDesde(): string | null {
  try {
    return localStorage.getItem(CHAVE_SESSAO_VIVA);
  } catch {
    return null;
  }
}

export interface SessaoGuardada {
  havia: boolean;
  /** há quantos segundos o token guardado tinha vencido. null = não dava pra saber. */
  vencidaHaS: number | null;
  /**
   * Havia REFRESH TOKEN no blob guardado? `null` = não deu pra ler.
   *
   * 🔴 ENTROU EM 17/08/2026 pelas 4 quedas do José em um dia, a cada ~1,5h — um
   * intervalo suspeito, porque o access token vive 1h. Com este campo, dois
   * defeitos que têm a MESMA cara passam a se separar sozinhos no relatório:
   * refresh PRESENTE + token vencido há pouco = ninguém renovou (auto-refresh
   * parado); refresh AUSENTE = não havia o que renovar.
   *
   * ⚠️ Só a PRESENÇA. O valor do refresh token não é lido, não é enviado e não
   * é registrado em lugar nenhum — é credencial.
   */
  temRefresh: boolean | null;
}

export function sessaoGuardada(): SessaoGuardada {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !/^sb-.+-auth-token$/.test(k)) continue;
      let vencidaHaS: number | null = null;
      let temRefresh: boolean | null = null;
      try {
        const blob = JSON.parse(localStorage.getItem(k) ?? "{}");
        const exp = Number(blob?.expires_at);
        if (Number.isFinite(exp)) vencidaHaS = Math.floor(Date.now() / 1000) - exp;
        // Só a PRESENÇA — o valor é credencial e não sai daqui.
        temRefresh = typeof blob?.refresh_token === "string" && blob.refresh_token.length > 0;
      } catch {
        /* token corrompido: o "havia" já é a informação que importa */
      }
      return { havia: true, vencidaHaS, temRefresh };
    }
  } catch {
    /* storage indisponível → responde "não sei" como "não" */
  }
  return { havia: false, vencidaHaS: null, temRefresh: null };
}

/**
 * O MOTIVO ESCRITO EM PORTUGUÊS, e por que ele precisa de mais que o `error`.
 *
 * Medido ao vivo em 14/08: no boot de verdade o `error` do `getSession()` chega
 * VAZIO. O cliente do supabase-js liga o auto-refresh na construção, tenta
 * renovar e limpa a chave ANTES do nosso efeito perguntar — quando perguntamos,
 * não há token nem erro, só ausência. Registrar "sem mensagem" nesse caso é
 * gastar a linha do log sem responder nada.
 *
 * Então o motivo é montado com o que ainda dá pra saber no instante certo: se o
 * token guardado já estava VENCIDO, e há quanto tempo. É justamente o dado que a
 * PARTE 3 (mexer no refresh) vai precisar pra decidir alguma coisa — "vencido há
 * 40 minutos" e "vencido há 9 dias" são defeitos diferentes.
 */
/**
 * O TOKEN AINDA VALE NESTE INSTANTE?
 *
 * É a pergunta que separa "perdi a sessão" de "o boot me mandou pro login com a
 * credencial boa na mão" (medido em produção, 15/08: token válido por mais uma
 * hora, painel no /login, e segundos depois ele carregou sozinho). Sem ela as
 * duas coisas caem no mesmo balde e o número conta boot como defeito de sessão —
 * que é exatamente o tipo de fantasma que a gente ia caçar.
 */
export function classeDaSaida(): "queda" | "flash_login" {
  const g = sessaoGuardada();
  return g.havia && g.vencidaHaS !== null && g.vencidaHaS < 0 ? "flash_login" : "queda";
}

export function motivoDaQuedaNoBoot(
  g: SessaoGuardada,
  mensagemDoErro?: string | null,
  vivaDesde?: string | null,
): string {
  const marco = vivaDesde ? ` (sessão estava viva desde ${vivaDesde})` : "";
  if (mensagemDoErro) return `token guardado recusado: ${mensagemDoErro}${marco}`;
  if (!g.havia) {
    // O caso de produção 15/08: o supabase-js já tinha apagado a chave dele
    // antes de perguntarmos. Sem o marco próprio, isto seria invisível.
    return `o supabase já não tinha token no boot e o painel caiu sem sessão${marco}`;
  }
  if (g.vencidaHaS === null) return `havia token guardado, sem validade legível, e o boot não teve sessão${marco}`;
  if (g.vencidaHaS <= 0) return `havia token guardado AINDA VÁLIDO e o boot não teve sessão${marco}`;
  const h = Math.floor(g.vencidaHaS / 3600);
  const quanto = h >= 24 ? `${Math.floor(h / 24)}d` : h >= 1 ? `${h}h` : `${Math.floor(g.vencidaHaS / 60)}min`;
  return `token guardado vencido há ${quanto} não foi renovado — o boot caiu sem sessão${marco}`;
}

export function limparQuedas(): void {
  try {
    localStorage.removeItem(CHAVE_QUEDA);
  } catch {
    /* idem */
  }
}

/**
 * A SEGUNDA METADE: no login bem-sucedido, entrega o que ficou guardado.
 *
 * ⚠️ SÓ LIMPA DEPOIS DE ENTREGAR. Se a rota falhar (rede ruim justamente no
 * primeiro segundo depois do login), o registro FICA e vai no próximo login. O
 * contrário — limpar antes, ou limpar sempre — apagaria a única prova que existe
 * do evento, num instrumento criado precisamente porque o evento não deixava
 * prova.
 *
 * Best-effort do começo ao fim: nada aqui pode atrapalhar o login de ninguém.
 */
export async function enviarQuedasPendentes(token: string | null): Promise<number> {
  const quedas = quedasPendentes();
  if (!token || quedas.length === 0) return 0;
  try {
    await apiFetch("/me/queda-de-sessao", token, {
      method: "POST",
      body: JSON.stringify({ quedas }),
    });
    limparQuedas();
    return quedas.length;
  } catch {
    return 0; // fica guardado pro próximo login
  }
}
